import { create } from 'zustand';

/**
 * Must match the key read by the inline bootstrap script in index.html, which
 * applies the stored theme before the first paint.
 */
export const THEME_STORAGE_KEY = 'thuiscoach.theme';

/**
 * Light or dark. There is deliberately no "system" option.
 *
 * The operating-system preference is still respected — it decides which theme a
 * first-time visitor gets — but from then on the choice is the user's and it
 * sticks. A third "follow the system" state made the switcher a three-way
 * control for a two-way decision, and left people unsure which theme they were
 * actually looking at.
 */
export type Theme = 'light' | 'dark';

interface ThemeState {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggle: () => void;
}

const prefersDarkQuery = '(prefers-color-scheme: dark)';

/** The theme a first-time visitor gets, taken from their OS. */
function systemTheme(): Theme {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }

    return window.matchMedia(prefersDarkQuery).matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme | null {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);

        return stored === 'light' || stored === 'dark' ? stored : null;
    } catch {
        // Private browsing can throw on access.
        return null;
    }
}

function persistTheme(theme: Theme): void {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // The choice simply does not survive a reload; the app still works.
    }
}

/**
 * Writes the theme onto <html> and keeps the browser chrome in step.
 */
function applyTheme(theme: Theme): void {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.setAttribute('data-theme', theme);

    /*
     * The browser chrome takes its colour from the canvas token, read back.
     *
     * It used to be two literals here — `#0c0e17` and `#f4f5fb` — which were the
     * *previous* palette's canvas values and stayed behind when the palette moved
     * to warm stone and teal. Every phone painted its status bar the colour of a
     * design system that no longer existed, and nothing could catch it: a hex in
     * a TypeScript file is invisible to the stylesheet checks.
     *
     * Reading the computed value means it cannot drift again. This runs after
     * `data-theme` is set, so the value is the one for the theme being applied.
     */
    const canvas = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-canvas')
        .trim();

    if (canvas !== '') {
        document
            .querySelector('meta[name="theme-color"]:not([media])')
            ?.setAttribute('content', canvas);
    }
}

const initialTheme = readStoredTheme() ?? systemTheme();

export const useThemeStore = create<ThemeState>((set, get) => ({
    theme: initialTheme,

    setTheme: (theme) => {
        persistTheme(theme);
        applyTheme(theme);
        set({ theme });
    },

    toggle: () => {
        get().setTheme(get().theme === 'dark' ? 'light' : 'dark');
    },
}));

/** The two options in the theme switcher, with their Dutch labels. */
export const THEME_OPTIONS: readonly { value: Theme; label: string }[] = [
    { value: 'light', label: 'Licht' },
    { value: 'dark', label: 'Donker' },
];
